import { DATE_FORMAT, DATE_TIME_FORMAT, DATE_VALUE_FORMAT, TIME_FORMAT, TIME_VALUE_FORMAT } from "../../constants/formats"
import _format from "date-fns/format";
import _parse from "date-fns/parse";
import _isPast from "date-fns/isPast";
import _isSameDay from "date-fns/isSameDay";
import _isBefore from "date-fns/isBefore"
import _add from "date-fns/add"

export const TIME_REGEX = /^[0-2][0-9]:[0-5][0-9]/;
export const DATE_REGEX = /^\d{4}\-(0?[1-9]|1[012])\-(0?[1-9]|[12][0-9]|3[01])$/;

/**
 * Invokes function safely inside try catch
 */
export const catchErrors = <T extends (...args: any[]) => any>(fn: T, args: Parameters<T>, fallback: any | ((error: any) => any)): ReturnType<T> => {
    try {
        return fn(...args);
    } catch (error) {
      //  console.info("[catchErrors]", error, { fn, args, fallback });
        return typeof fallback === "function" ? fallback(error) : fallback;
    };
};

export const PickerUtils = {

    /**
     * Formats picker value to separate time and date inputs texts
     * @param value 
     * @returns 
     */
    separateDateAndTime: (value?: string): { date?: Date, time?: Date, dateInput?: string, timeInput?: string } => {
        if (!value) {
            return {}
        };

        if (RegExp(TIME_REGEX).test(value)) {
            return {
                timeInput: value,
                time: PickerUtils.parseString(value, TIME_FORMAT)
            };
        };

        const date = new Date(value);

        if (RegExp(DATE_REGEX).test(value)) {
            return {
                date: date,
                dateInput: PickerUtils.formatDateString(value, DATE_FORMAT)
            };
        };

        return {
            date: date,
            time: date,
            dateInput: PickerUtils.formatDate(date, DATE_FORMAT),
            timeInput: PickerUtils.formatDate(date, TIME_FORMAT)
        };
    },

    guessDateFormat: (value: string): "time" | "date" | "date-time" | undefined => {
        if (RegExp(TIME_REGEX).test(value)) {
            return "time";
        };
        if (RegExp(DATE_REGEX).test(value)) {
            return "date";
        };
        const date = new Date(value);
        if (date) {
            return "date-time";
        };
    },



    ensureValueFormat: (value: string, format: "time" | "date" | "date-time"): string | undefined => {
        const date = PickerUtils.valueToDate(value);
        if (!date) return
        if (format === "date") {
            return PickerUtils.formatDate(date, DATE_VALUE_FORMAT);
        };
        if (format === "time") {
            return PickerUtils.formatDate(date, TIME_VALUE_FORMAT);
        };
        if (format === "date-time") {
            return date.toISOString()
        };
    },


    /**
     * Formats date object to string format
     */
    formatDate: (date: Date, format: string) => {
        return _format(date, format);
    },

    /**
     * Formats date string to a input text
     */
    formatDateString: (value: string, format: string) => {
        return _format(new Date(value), format)
    },

    /**
     * Parses input text to a date object
     */
    parseString: (text: string, format: string, referenceDate = new Date()) => {
        return _parse(text, format, referenceDate);
    },

    /**
     * Converts date string to date object
     */
    valueToDate: (text?: string): Date | undefined => {
        if (text && RegExp(/^[0-2][0-9]:[0-5][0-9]/).test(text)) {
            const [hours, minutes] = text.split(":");
            const date = PickerUtils.resetTime(new Date());
            date.setHours(Number(hours))
            date.setMinutes(Number(minutes))
            return date
        };
        return text ? new Date(text) : undefined
    },


    dateValueToFormat: (value: string | undefined, schemaFormat: "date" | "time" | "date-time") => {

        const valueAsDate = PickerUtils.valueToDate(value);
        if (!valueAsDate) {
            return;
        }

        if (schemaFormat === "date") {
            return PickerUtils.formatDate(valueAsDate, DATE_FORMAT);
        }

        if (schemaFormat === "time") {
            return PickerUtils.formatDate(valueAsDate, TIME_FORMAT);
        }

        if (schemaFormat === "date-time") {
            return PickerUtils.formatDate(valueAsDate, DATE_TIME_FORMAT);
        }
    },


    /**
     * Coverts input text to date object
     */
    inputTextToDate: (inputText: string | undefined, format: string, referenceDate = new Date()): Date | undefined => {
        if (inputText) {
            return _parse(inputText, format, referenceDate)
        };
    },

    /**
     * Override only date of date object
     */
    overwriteDatePartOfDate: (date: Date, newDate: Date) => {
        date.setDate(newDate.getDate());
        date.setMonth(newDate.getMonth());
        date.setFullYear(newDate.getFullYear());
        return new Date(date);
    },

    /**
     * Override only time of date objects
     */
    overwriteTimePartOfDate: (date: Date, newDate: Date) => {
        date.setHours(newDate.getHours());
        date.setMinutes(newDate.getMinutes());
        date.setMilliseconds(newDate.getMilliseconds());
        return new Date(date)
    },

    /**
     * Sets time part of date to 00:00:00:0000
     */
    resetTime: (date: Date) => {
        date.setHours(0);
        date.setMinutes(0);
        date.setSeconds(0);
        date.setMilliseconds(0);
        return new Date(date)
    },

    /**
     * Rounds date to nearest hour
     */
    roundToNearestHour: (date: Date, hourRoundingValue: number = 1) => {
        date.setHours(date.getHours() + hourRoundingValue);
        date.setMinutes(0);
        date.setSeconds(0);
        date.setMilliseconds(0);
        return new Date(date)
    },

    isValidDate: (date?: Date) => {
        return date && !isNaN(date.getTime())
    },

    /**
     * Creates new instance of date
     * @param inputDate 
     * @returns 
     */
    ensureFreshDateInstance: (inputDate?: Date) => {
        return (inputDate && PickerUtils.isValidDate(inputDate)) ? new Date(inputDate.getTime()) : undefined
    },


    /**
     * Takes reference date and calculates default value
     */
    getDefaultDateValue: (reference: Date, options: { defaultTime?: string; hourRoundingValue?: number; minDate?: Date; }): string | undefined => {
        let date = PickerUtils.ensureFreshDateInstance(reference);
        if (date) {
            let time = date;
            if (options.defaultTime && RegExp(TIME_REGEX).test(options.defaultTime)) {
                const [hour, minutes] = options.defaultTime.split(":").map(Number);
                time = PickerUtils.resetTime(date);
                time.setHours(hour);
                time.setMinutes(minutes);
            } else {
                time = PickerUtils.roundToNearestHour(time, options.hourRoundingValue);
            };

            if (options.minDate && (_isBefore(time, options.minDate) || PickerUtils.formatDate(options.minDate, TIME_FORMAT) === PickerUtils.formatDate(time, TIME_FORMAT))) {
                date = _add(date, { days: 1 }); // _add day if time is before reference
            };

            date = PickerUtils.overwriteTimePartOfDate(date, time);
            if (date) {
                return date.toISOString()
            }
        };
    },

    /**
     * Takes reference start - end values, and calculates default values for them.
     */
    getDefaultDateRangeValues: (value: { start?: string, end?: string; } | undefined, options: { start: { defaultTime?: string; hourRoundingValue?: number; }; end: { defaultTime?: string; hourRoundingValue?: number; } }) => {
        const reference = new Date();

        let start = value?.start;
        let startDate = catchErrors(PickerUtils.valueToDate, [start], undefined)

        if (!start || !startDate) {
            start = PickerUtils.getDefaultDateValue(reference, {
                minDate: reference,
                defaultTime: options.start.defaultTime,
                hourRoundingValue: options.start.hourRoundingValue
            });
            startDate = PickerUtils.valueToDate(start)
        };

        let end = value?.end;

        if (!end && startDate) {
            end = PickerUtils.getDefaultDateValue(startDate, {
                minDate: startDate,
                defaultTime: options.end.defaultTime,
                hourRoundingValue: options.end.hourRoundingValue
            });
        };

        return { end, start };
    },


    /**
     * Takes date and time inputs and joins them to value format
     */
    processValue: (
        dateInput: string | undefined,
        timeInput: string | undefined,
        valueFormat: "date" | "time" | "date-time"
    ): string | undefined => {

        const dateAsDate = dateInput ? PickerUtils.parseString(dateInput, DATE_FORMAT) : undefined;
        const timeAsDate = timeInput ? PickerUtils.parseString(timeInput, TIME_FORMAT) : undefined;

        if (valueFormat === "date-time" && dateAsDate && timeAsDate) {
            let newValue = PickerUtils.resetTime(new Date());
            newValue = PickerUtils.overwriteDatePartOfDate(newValue, dateAsDate);
            newValue = PickerUtils.overwriteTimePartOfDate(newValue, timeAsDate);
            return newValue.toISOString();
        };

        if ((valueFormat === "date" && dateAsDate) || dateAsDate) {
            return PickerUtils.formatDate(dateAsDate, DATE_VALUE_FORMAT);
        };

        if ((valueFormat === "time" && timeAsDate) || timeAsDate) {
            return PickerUtils.formatDate(timeAsDate, TIME_VALUE_FORMAT);
        };
    }
};